import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Typography, Space, Avatar, Upload } from 'antd';
import {
  SendOutlined,
  UserOutlined,
  CameraOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
  AudioOutlined,
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { extractList, extractObject } from '../utils/formatters';

const { TextArea } = Input;
const { Title } = Typography;

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(
  /\/api$/,
  '',
);

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const Chat = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [message, setMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef(null);

  const [pendingMedia, setPendingMedia] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    const conversationWith = searchParams.get('conversation_with');

    if (conversationWith) {
      // Handle conversation_with parameter (from notifications)
      const tempConv = {
        id: `conversation_with_${conversationWith}`, // Unique ID for conversation_with
        contact_id: parseInt(conversationWith),
        contact_name: 'Loading...',
      };
      setSelectedConversation(tempConv);

      // Fetch messages using conversation_with parameter
      api
        .get('/provider/chat-messages?conversation_with=' + conversationWith)
        .then((resp) => {
          if (resp.data.data && resp.data.data.length > 0) {
            const firstMsg = resp.data.data[0];
            const otherName =
              firstMsg.sender_id === parseInt(conversationWith)
                ? firstMsg.receiver_name
                : firstMsg.sender_name;
            setSelectedConversation({ ...tempConv, contact_name: otherName || 'User' });
          } else {
            setSelectedConversation({ ...tempConv, contact_name: 'User' });
          }
        })
        .catch(() => {
          setSelectedConversation({ ...tempConv, contact_name: 'User' });
        });
    } else if (conversationId) {
      const tempConv = {
        id: parseInt(conversationId),
        contact_id: parseInt(conversationId),
        contact_name: 'Loading...',
      };
      setSelectedConversation(tempConv);

      api
        .get('/provider/conversations/' + conversationId + '/messages')
        .then((resp) => {
          if (resp.data.data && resp.data.data.length > 0) {
            const firstMsg = resp.data.data[0];
            const otherName =
              firstMsg.sender_id === parseInt(conversationId)
                ? firstMsg.sender_name
                : firstMsg.receiver_name;
            setSelectedConversation({ ...tempConv, contact_name: otherName || 'Customer' });
          } else {
            setSelectedConversation({ ...tempConv, contact_name: 'Customer' });
          }
        })
        .catch(() => {
          setSelectedConversation({ ...tempConv, contact_name: 'Customer' });
        });
    }
  }, [searchParams]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const { isLoading: conversationsLoading } = useQuery('conversations', async () => {
    const response = await api.get('/provider/conversations');
    // The endpoint may answer with a bare array or a `{ data }` envelope.
    const data = extractList(response.data);
    setConversations(data);
    return data;
  });

  const { isLoading: messagesLoading } = useQuery(
    ['messages', selectedConversation?.id],
    async () => {
      if (!selectedConversation) return [];
      const response = await api.get(
        '/provider/conversations/' + selectedConversation.id + '/messages',
      );
      const data = extractList(response.data);
      setMessages(data);
      return data;
    },
    {
      enabled: !!selectedConversation,
    },
  );

  const sendMessageMutation = useMutation(
    (messageData) => {
      if (!messageData.customer_id) {
        return Promise.reject(new Error('No customer selected'));
      }
      return api.post('/provider/send-message', messageData);
    },
    {
      onSuccess: (response) => {
        const created = extractObject(response.data);
        if (created) setMessages((prev) => [...prev, created]);
        setMessage('');
        queryClient.invalidateQueries('conversations');
        queryClient.invalidateQueries(['messages', selectedConversation?.id]);
      },
      onError: () => {
        message.error('Failed to send message');
      },
    },
  );

  const handleSendMessage = () => {
    if (message.trim() && selectedConversation) {
      const customerId = selectedConversation.contact_id || selectedConversation.id;
      sendMessageMutation.mutate({
        customer_id: customerId,
        message: message.trim(),
        type: 'provider',
      });
    }
  };

  const handleMediaChange = (type) => (file) => {
    if (!selectedConversation) {
      message.warning('Please select a conversation first');
      return false;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPendingMedia({
        type: type,
        file: file,
        preview: reader.result,
      });
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleSendMedia = () => {
    if (!pendingMedia || !selectedConversation) return;

    const customerId = selectedConversation.contact_id || selectedConversation.id;
    const formData = new FormData();

    if (pendingMedia.type === 'image') {
      formData.append('image_url', pendingMedia.file);
    } else if (pendingMedia.type === 'video') {
      formData.append('video_url', pendingMedia.file);
    } else if (pendingMedia.type === 'voice') {
      formData.append('voice_url', pendingMedia.file);
    }
    formData.append('customer_id', customerId);
    formData.append('message', `Sent a ${pendingMedia.type}`);

    api
      .post('/provider/send-message', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => {
        const created = extractObject(response.data);
        if (created) {
          setMessages((prev) => [...prev, created]);
          queryClient.invalidateQueries('conversations');
        }
        message.success(`${pendingMedia.type} sent`);
      })
      .catch(() => {
        message.error(`Failed to send ${pendingMedia.type}`);
      });

    setPendingMedia(null);
  };

  const handleCancelMedia = () => {
    setPendingMedia(null);
  };

  const handleLocationShare = () => {
    if (!selectedConversation) {
      message.warning('Please select a conversation first');
      return;
    }

    if (!navigator.geolocation) {
      message.error('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const customerId = selectedConversation.contact_id || selectedConversation.id;

        api
          .post('/provider/send-message', {
            customer_id: customerId,
            message: 'Shared location',
            location_lat: position.coords.latitude,
            location_lng: position.coords.longitude,
            location_name: 'Current Location',
          })
          .then((response) => {
            const created = extractObject(response.data);
            if (created) {
              setMessages((prev) => [...prev, created]);
              queryClient.invalidateQueries('conversations');
            }
            message.success('Location shared');
          })
          .catch(() => {
            message.error('Failed to share location');
          });
      },
      () => {
        message.error('Unable to get your location');
      },
    );
  };

  const startRecording = async () => {
    if (!selectedConversation) {
      message.warning('Please select a conversation first');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = () => {
          setPendingMedia({
            type: 'voice',
            file: audioBlob,
            preview: reader.result,
          });
        };
        reader.readAsDataURL(audioBlob);

        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingDuration(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      message.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
    setRecordingDuration(0);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return '';
    }
  };

  const renderMessageContent = (msg) => {
    const isLocation = msg.message_type === 'location';
    const isImage = msg.message_type === 'image';
    const isVideo = msg.message_type === 'video';
    const isVoice = msg.message_type === 'voice';

    if (isLocation) {
      let locationData = msg.location_data;
      if (typeof locationData === 'string') {
        try {
          locationData = JSON.parse(locationData);
        } catch (e) {
          locationData = null;
        }
      }

      const lat = locationData?.latitude;
      const lng = locationData?.longitude;
      const address = locationData?.address || locationData?.name || 'Shared Location';
      const mapUrl = lat && lng ? `https://maps.google.com/?q=${lat},${lng}` : null;

      return (
        <div style={{ padding: 8, background: '#e6f7ff', borderRadius: 8, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <EnvironmentOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 500 }}>Location</div>
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{address}</div>
              {lat && lng && (
                <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                  {lat.toFixed(6)}, {lng.toFixed(6)}
                </div>
              )}
            </div>
          </div>
          {mapUrl && (
            <div style={{ marginTop: 8 }}>
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 11,
                  display: 'block',
                  textAlign: 'center',
                  background: '#1890ff',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: 4,
                  textDecoration: 'none',
                }}
              >
                View on Google Maps
              </a>
              <iframe
                src={`https://www.google.com/maps?q=${lat},${lng}&output=embed&zoom=15`}
                width="100%"
                height="100"
                style={{ border: 0, marginTop: 8, borderRadius: 8 }}
                loading="lazy"
                title="Location Map"
              />
            </div>
          )}
        </div>
      );
    }

    if (isImage && msg.image_url) {
      const imgSrc = getFullUrl(msg.image_url);
      return (
        <div>
          <img
            src={imgSrc}
            alt="Shared"
            style={{ maxWidth: 250, maxHeight: 250, borderRadius: 8, cursor: 'pointer' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = imgSrc;
            }}
            onClick={() => window.open(imgSrc, '_blank')}
          />
        </div>
      );
    }

    if (isVideo && msg.video_url) {
      return (
        <div>
          <video
            src={getFullUrl(msg.video_url)}
            controls
            style={{ maxWidth: 250, borderRadius: 8 }}
          />
        </div>
      );
    }

    if (isVoice && msg.voice_url) {
      return (
        <div>
          <audio src={getFullUrl(msg.voice_url)} controls style={{ width: 200 }} />
        </div>
      );
    }

    return <div>{msg.message}</div>;
  };

  return (
    <div style={{ padding: 24, background: '#fff', minHeight: 'calc(100vh - 150px)' }}>
      <Title level={2} style={{ marginBottom: 24 }}>
        Customer Chat
      </Title>

      <div
        style={{
          display: 'flex',
          gap: 16,
          height: '600px',
          border: '1px solid #d9d9d9',
          borderRadius: 8,
        }}
      >
        {/* Conversations List */}
        <div
          style={{
            width: 300,
            borderRight: '1px solid #d9d9d9',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: '16px', borderBottom: '1px solid #d9d9d9' }}>
            <Title level={4} style={{ margin: 0 }}>
              Conversations
            </Title>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {conversationsLoading ? (
              <div style={{ padding: 20, textAlign: 'center' }}>Loading...</div>
            ) : conversations.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
                No conversations
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor:
                      selectedConversation?.id === conv.id ? '#e6f7ff' : 'transparent',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <span style={{ fontWeight: selectedConversation?.id === conv.id ? 600 : 400 }}>
                      {conv.contact_name || 'Unknown'}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: '#999',
                      marginTop: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {conv.last_message}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedConversation ? (
            <>
              <div
                style={{
                  padding: '16px',
                  borderBottom: '1px solid #d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <Avatar icon={<UserOutlined />} />
                <Title level={5} style={{ margin: 0 }}>
                  {selectedConversation.contact_name || 'Customer'}
                </Title>
              </div>

              <div style={{ flex: 1, overflow: 'auto', padding: 16, background: '#fafafa' }}>
                {messagesLoading ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    No messages yet
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        justifyContent: msg.sender_id === 26 ? 'flex-end' : 'flex-start',
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '10px 14px',
                          borderRadius: 12,
                          backgroundColor: msg.sender_id === 26 ? '#1890ff' : '#fff',
                          color: msg.sender_id === 26 ? '#fff' : '#262626',
                          border: msg.sender_id !== 26 ? '1px solid #e8e8e8' : 'none',
                        }}
                      >
                        {renderMessageContent(msg)}
                        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                          {formatTime(msg.created_at)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div style={{ padding: 16, borderTop: '1px solid #d9d9d9', background: '#fff' }}>
                {/* Recording indicator */}
                {isRecording && (
                  <div
                    style={{
                      padding: '8px 16px',
                      borderTop: '1px solid #e8f0e8',
                      background: '#fff1f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#ff4d4f',
                        animation: 'pulse 1s infinite',
                      }}
                    />
                    <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                      Recording... {Math.floor(recordingDuration / 60)}:
                      {String(recordingDuration % 60).padStart(2, '0')}
                    </span>
                    <Button
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={stopRecording}
                      style={{ background: '#52c41a', color: '#fff', borderColor: '#52c41a' }}
                    >
                      Stop
                    </Button>
                    <Button size="small" icon={<CloseOutlined />} onClick={cancelRecording}>
                      Cancel
                    </Button>
                  </div>
                )}

                {/* Pending Media Preview */}
                {pendingMedia && !isRecording && (
                  <div
                    style={{
                      marginBottom: 8,
                      padding: 8,
                      background: '#f5f5f5',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    {pendingMedia.type === 'image' && (
                      <img
                        src={pendingMedia.preview}
                        alt="Preview"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                      />
                    )}
                    {pendingMedia.type === 'video' && (
                      <video
                        src={pendingMedia.preview}
                        style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4 }}
                      />
                    )}
                    {pendingMedia.type === 'voice' && (
                      <div
                        style={{
                          padding: 8,
                          background: '#e6f7ff',
                          borderRadius: 4,
                          textAlign: 'center',
                        }}
                      >
                        <AudioOutlined style={{ fontSize: 24, color: '#52b788' }} />
                        <div style={{ fontSize: 12 }}>
                          Voice ({Math.round(pendingMedia.file.size / 1024)} KB)
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={handleSendMedia}
                      >
                        Send
                      </Button>
                      <Button size="small" icon={<CloseOutlined />} onClick={handleCancelMedia}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Attachment Buttons */}
                <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleMediaChange('image')}
                    disabled={!!pendingMedia || isRecording || !selectedConversation}
                  >
                    <Button type="text" icon={<CameraOutlined />} title="Send Image" />
                  </Upload>
                  <Upload
                    accept="video/*"
                    showUploadList={false}
                    beforeUpload={handleMediaChange('video')}
                    disabled={!!pendingMedia || isRecording || !selectedConversation}
                  >
                    <Button type="text" icon={<VideoCameraOutlined />} title="Send Video" />
                  </Upload>
                  <Button
                    type="text"
                    icon={<EnvironmentOutlined />}
                    onClick={handleLocationShare}
                    title="Share Location"
                    disabled={!!pendingMedia || isRecording || !selectedConversation}
                  />
                  <Button
                    type="text"
                    icon={<AudioOutlined />}
                    onClick={startRecording}
                    title="Record Audio"
                    disabled={isRecording || !!pendingMedia || !selectedConversation}
                  />
                </div>

                <Space.Compact style={{ width: '100%' }}>
                  <TextArea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type a message..."
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    style={{ borderRadius: '4px 0 0 4px' }}
                  />
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    loading={sendMessageMutation.isLoading}
                    style={{ height: 'auto', minHeight: 42, borderRadius: '0 4px 4px 0' }}
                  >
                    Send
                  </Button>
                </Space.Compact>
              </div>
            </>
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
              }}
            >
              Select a conversation to start chatting
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;
